import { validatedDisplayView } from "../../i18n/display-view.js";
import { portalText } from "../../i18n/portal.js";
/**
 * Area `outbound-conversation` of the console gateway — "ring a number now" through the account's own
 * SIP trunk (plan-008, design §22.13.2 IC-77, DD8-26). Written by ST-UN, the sole owner of the gateway
 * files in plan-008; ST-UA's Outbound tab consumes it.
 *
 * ── ONE KEY, ONE RING ──────────────────────────────────────────────────────────────────────
 * `startOutboundConversation` REQUIRES an Idempotency-Key, and the API never rings a number twice for
 * one key. So the key is the caller's, not minted here per call: the dialog makes one when it opens and
 * sends the same one if the member presses again after a lost answer — a fresh key per press would turn
 * a network blip into a second ring. After `503 agent_runtime_unavailable` the API keeps answering that
 * for the key; whether the conversation started is then read in History, never retried blind.
 *
 * ── THE CODEC IS OBSERVED, NEVER DEFAULTED ───────────────────────────────────────────────────
 * `sip_leg` (ST-AN's `SipLegSummary`, one definition in the conversations area) carries the codec, its
 * rate and `hd` only as observed after answer; each is `null` otherwise, and the view keeps the `null`.
 *
 * ── THE WAIT ─────────────────────────────────────────────────────────────────────────────────
 * A `429` from `startOutboundConversation` is raised as a `RetryAfterApiError` with the answer's
 * `Retry-After` (`../retry-after.ts`, plan-008 ST-RV2), so the dialog can say how long to wait.
 */
import { assertOk, type ApiResult, type WeTalkApiClient } from "@wetalk/api-client";
import { OUTBOUND_ORIGIN, type OutboundOrigin } from "@wetalk/domain";

import type { ConsoleGateway } from "../gateway.js";
import { MalformedResponseError, readBoolean, readRecord, readText, readWholeNumber } from "../decode.js";
import { refuseWithRetryAfter } from "../retry-after.js";
import type { GatewayGap, SliceContext } from "./slice-context.js";

export interface OutboundConversationStartRequest {
  /** The number to ring, E.164 with its `+`. */
  readonly partyNumber: string;
  /** A caller-ID number of a verified, enabled trunk, bound to this voice agent. */
  readonly callerIdPhoneNumberId: string;
  /** 5 … 80; the API's default (30) when left out. */
  readonly ringSecond?: number;
  /** The member's own label, shown in History; never given to the person called. */
  readonly reference?: string;
  /** Made once per dialog, and sent again on a retry — see the header. */
  readonly idempotencyKey: string;
}

export interface OutboundConversationStartedView {
  readonly conversationId: string;
  readonly state: "dialing";
}

/** ST-AN's `SipLegSummary`: what a trunk leg was observed to use. */
export interface SipLegSummaryView {
  readonly sipTrunkId: string;
  readonly sipTrunkDisplayName: string;
  readonly direction: "inbound" | "outbound";
  readonly codec: "G722" | "PCMU" | "PCMA" | "AMR-WB" | null;
  readonly sampleRateHz: number | null;
  /** `null` when the codec was not observed — never guessed from the trunk's settings. */
  readonly hd: boolean | null;
  readonly transport: "udp" | "tcp" | "tls" | null;
  readonly mediaEncryption: "none" | "srtp" | null;
  readonly outboundOrigin: OutboundOrigin | null;
  readonly outboundReference: string | null;
}

export interface OutboundConversationView {
  readonly conversationId: string;
  readonly voiceAgentId: string;
  readonly state: "dialing" | "in_progress" | "ended";
  readonly endReason: string | null;
  readonly answeredAtText: string | null;
  readonly endedAtText: string | null;
  readonly billedSecond: number | null;
  readonly outboundOrigin: OutboundOrigin;
  readonly outboundReference: string | null;
  /** Absent for a leg that did not come through a trunk. */
  readonly sipLeg: SipLegSummaryView | null;
}

/** A 2xx answer the console could not decode, with the request id to quote. */
export class OutboundConversationReadError extends Error {
  override readonly name = "OutboundConversationReadError";
  readonly requestId: string | null;
  constructor(cause: Error, requestId: string | null) {
    super(portalText("shell", "wetalk_answered_in_a_way_this_page_cannot_read_value_65d8fa72", { v0: String(cause.message) }));
    this.requestId = requestId;
  }
}

declare module "../gateway.js" {
  interface ConsoleGateway {
    /**
     * Rings `partyNumber` now from the voice agent, through the trunk of the caller-ID number. Billed
     * talk time from the answer. Refused with nothing dialled: `destination_refused`,
     * `caller_id_not_bound`, `sip_trunk_unverified`, `sip_trunk_disabled`, the suppression lists, the
     * balance (402), `rate_limited` / `daily_ceiling_reached` (429), a resting model (503).
     */
    startOutboundConversation(
      voiceAgentId: string,
      request: OutboundConversationStartRequest,
    ): Promise<OutboundConversationStartedView>;
    getOutboundConversation(conversationId: string): Promise<OutboundConversationView>;
    /** Hangs up one still ringing or in progress; ending an ended one resolves too. */
    endOutboundConversation(conversationId: string): Promise<void>;
  }
}

export type OutboundConversationGatewaySlice = Pick<
  ConsoleGateway,
  "startOutboundConversation" | "getOutboundConversation" | "endOutboundConversation"
>;

/** Empty: ST-AO documented every operation this slice calls. */
export const outboundConversationGap: readonly GatewayGap[] = [];

function oneOf<T extends string>(value: unknown, path: string, allowed: readonly T[]): T {
  const raw = readText(value, path);
  const found = allowed.find((one) => one === raw);
  if (found === undefined) throw new MalformedResponseError(path, allowed.join(" | "), raw);
  return found;
}

function nullableOneOf<T extends string>(value: unknown, path: string, allowed: readonly T[]): T | null {
  return value === null ? null : oneOf(value, path, allowed);
}

function nullableText(value: unknown, path: string): string | null {
  return value === null ? null : readText(value, path);
}

function nullableNumber(value: unknown, path: string): number | null {
  return value === null ? null : readWholeNumber(value, path);
}

export function sipLegSummaryOfWire(value: unknown, path: string): SipLegSummaryView {
  const one = readRecord(value, path);
  const hd = one["hd"];
  return {
    sipTrunkId: readText(one["sip_trunk_id"], `${path}.sip_trunk_id`),
    sipTrunkDisplayName: readText(one["sip_trunk_display_name"], `${path}.sip_trunk_display_name`),
    direction: oneOf(one["direction"], `${path}.direction`, ["inbound", "outbound"] as const),
    codec: nullableOneOf(one["codec"], `${path}.codec`, ["G722", "PCMU", "PCMA", "AMR-WB"] as const),
    sampleRateHz: nullableNumber(one["sample_rate_hz"], `${path}.sample_rate_hz`),
    hd: hd === null ? null : readBoolean(hd, `${path}.hd`),
    transport: nullableOneOf(one["transport"], `${path}.transport`, ["udp", "tcp", "tls"] as const),
    mediaEncryption: nullableOneOf(one["media_encryption"], `${path}.media_encryption`, ["none", "srtp"] as const),
    outboundOrigin: nullableOneOf(one["outbound_origin"], `${path}.outbound_origin`, OUTBOUND_ORIGIN),
    outboundReference: nullableText(one["outbound_reference"], `${path}.outbound_reference`),
  };
}

export function outboundConversationOfWire(
  value: unknown,
  path: string,
  when: (isoTimestamp: string) => string,
): OutboundConversationView {
  const one = readRecord(value, path);
  const answeredAt = one["answered_at"];
  const endedAt = one["ended_at"];
  const sipLeg = one["sip_leg"];
  return validatedDisplayView({
    conversationId: readText(one["conversation_id"], `${path}.conversation_id`),
    voiceAgentId: readText(one["voice_agent_id"], `${path}.voice_agent_id`),
    state: oneOf(one["state"], `${path}.state`, ["dialing", "in_progress", "ended"] as const),
    endReason: nullableText(one["end_reason"], `${path}.end_reason`),
    get answeredAtText() { return answeredAt === null ? null : when(readText(answeredAt, `${path}.answered_at`)); },
    get endedAtText() { return endedAt === null ? null : when(readText(endedAt, `${path}.ended_at`)); },
    billedSecond: nullableNumber(one["billed_second"], `${path}.billed_second`),
    outboundOrigin: oneOf(one["outbound_origin"], `${path}.outbound_origin`, OUTBOUND_ORIGIN),
    outboundReference: nullableText(one["outbound_reference"], `${path}.outbound_reference`),
    sipLeg: sipLeg === undefined || sipLeg === null ? null : sipLegSummaryOfWire(sipLeg, `${path}.sip_leg`),
  });
}

function decodedOf<T>(result: ApiResult, decode: (body: unknown) => T): T {
  const body = assertOk(result);
  try {
    return decode(body);
  } catch (error) {
    if (error instanceof MalformedResponseError) throw new OutboundConversationReadError(error, result.requestId);
    throw error;
  }
}

export function outboundConversationApiSlice(
  client: WeTalkApiClient,
  context: SliceContext,
): OutboundConversationGatewaySlice {
  const when = (isoTimestamp: string): string => context.formatWhen(isoTimestamp, context.now());
  return {
    async startOutboundConversation(voiceAgentId, request): Promise<OutboundConversationStartedView> {
      const body: Record<string, unknown> = {
        party_number: request.partyNumber,
        caller_id_phone_number_id: request.callerIdPhoneNumberId,
      };
      if (request.ringSecond !== undefined) body["ring_second"] = request.ringSecond;
      if (request.reference !== undefined) body["reference"] = request.reference;
      const result = await client.startOutboundConversation({
        path: { voice_agent_id: voiceAgentId },
        header: { "idempotency-key": request.idempotencyKey },
        body,
      });
      // `429 rate_limited` / `daily_ceiling_reached` keep their real `Retry-After` (plan-008 ST-RV2).
      refuseWithRetryAfter(result, context.takeRetryAfterSecond);
      return decodedOf(result, (answer) => {
        const data = readRecord(readRecord(answer, "startOutboundConversation")["data"], "startOutboundConversation.data");
        return {
          conversationId: readText(data["conversation_id"], "startOutboundConversation.data.conversation_id"),
          state: oneOf(data["state"], "startOutboundConversation.data.state", ["dialing"] as const),
        };
      });
    },

    async getOutboundConversation(conversationId: string): Promise<OutboundConversationView> {
      const result = await client.getOutboundConversation({ path: { conversation_id: conversationId } });
      return decodedOf(result, (body) =>
        outboundConversationOfWire(readRecord(body, "getOutboundConversation")["data"], "getOutboundConversation.data", when),
      );
    },

    async endOutboundConversation(conversationId: string): Promise<void> {
      assertOk(await client.endOutboundConversation({ path: { conversation_id: conversationId }, body: {} }));
    },
  };
}
